"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { InfoTooltip } from "@/components/ui/tooltip";
import type { FormField, FormFieldGroupWithFields, LabelStyle } from "@/types";
import { useIsDarkMode } from "@/lib/theme/ThemeProvider";
import { cn, linkifyText, parseBBCode } from "@/lib/utils";

// Helper function to generate Tailwind classes from label style
function getLabelClassName(labelStyle?: LabelStyle | null): string {
  if (!labelStyle) return "";
  
  const classes: string[] = [];
  
  if (labelStyle.fontSize) {
    classes.push(`text-${labelStyle.fontSize}`);
  }
  if (labelStyle.fontWeight) {
    classes.push(`font-${labelStyle.fontWeight}`);
  }
  if (labelStyle.fontStyle === "italic") {
    classes.push("italic");
  }
  if (labelStyle.textTransform && labelStyle.textTransform !== "none") {
    classes.push(labelStyle.textTransform);
  }
  if (labelStyle.color && labelStyle.color !== "default" && labelStyle.color !== "custom") {
    const colorMap: Record<string, string> = {
      primary: "text-primary",
      secondary: "text-secondary-foreground",
      muted: "text-muted-foreground",
      destructive: "text-destructive",
    };
    if (colorMap[labelStyle.color]) {
      classes.push(colorMap[labelStyle.color]);
    }
  }
  if (labelStyle.customClassName) {
    classes.push(labelStyle.customClassName);
  }
  
  return classes.join(" ");
}

// Helper function to get inline style for custom color
function getLabelInlineStyle(labelStyle?: LabelStyle | null): React.CSSProperties | undefined {
  if (labelStyle?.color === "custom" && labelStyle.customColor) {
    return { color: labelStyle.customColor };
  }
  return undefined;
}

// Helper function to get layout class for field container
function getLayoutClass(layout: string | undefined): string {
  switch (layout) {
    case "horizontal":
      return "grid grid-cols-12 gap-4";
    case "grid-2":
      return "grid grid-cols-12 gap-4";
    case "grid-3":
      return "grid grid-cols-12 gap-4";
    default:
      return "grid grid-cols-12 gap-4";
  }
}

// Helper function to get field width class based on individual field width or group layout
function getFieldWidthClass(fieldWidth: string | null | undefined, groupLayout: string | undefined): string {
  // If field has explicit width, use it
  if (fieldWidth) {
    switch (fieldWidth) {
      case "half":
        return "col-span-12 sm:col-span-6";
      case "third":
        return "col-span-12 sm:col-span-6 lg:col-span-4";
      case "quarter":
        return "col-span-12 sm:col-span-6 lg:col-span-3";
      default:
        return "col-span-12";
    }
  }
  
  // Otherwise, use the group layout default
  switch (groupLayout) {
    case "horizontal":
      return "col-span-12 sm:col-span-6 lg:col-span-4";
    case "grid-2":
      return "col-span-12 sm:col-span-6";
    case "grid-3":
      return "col-span-12 sm:col-span-6 lg:col-span-4";
    case "grid-4":
      return "col-span-12 sm:col-span-6 lg:col-span-3";
    case "grid-5":
      return "col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-2";
    default:
      return "col-span-12";
  }
}

// Helper function to get card style class
function getCardStyleClass(cardStyle: string | undefined): string {
  switch (cardStyle) {
    case "bordered":
      return "border rounded-lg p-4";
    case "elevated":
      return "shadow-md rounded-lg p-4 bg-card";
    case "minimal":
      return "";
    default:
      return "bg-muted/30 rounded-lg p-4";
  }
}

// Helper function to get children grid class based on children_per_row
function getChildrenGridClass(childrenPerRow: number | undefined): string {
  switch (childrenPerRow) {
    case 2:
      return "grid-cols-1 lg:grid-cols-2";
    case 3:
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    default:
      return "grid-cols-1";
  }
}

// Helper function to check if a value is considered "filled"
function isValueFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (value === "") return false;
  if (value === false) return false; // Unchecked checkbox is not "filled"
  if (Array.isArray(value) && (value.length === 0 || value.every(v => v === ""))) return false;
  return true;
}

// Helper function to collect all fields from a group and its children recursively
function collectAllFieldsFromGroupStatic(group: FormFieldGroupWithFields): FormField[] {
  const fields = [...group.fields];
  if (group.children) {
    for (const child of group.children) {
      fields.push(...collectAllFieldsFromGroupStatic(child));
    }
  }
  return fields;
}

// Component to render a form field group with layout options
interface FormGroupRendererProps {
  group: FormFieldGroupWithFields;
  isFieldVisible: (field: FormField) => boolean;
  isGroupVisible: (group: FormFieldGroupWithFields) => boolean;
  renderField: (field: FormField) => React.ReactNode;
  errors: Record<string, unknown>;
  touchedFields?: Partial<Readonly<Record<string, boolean>>>;
  isSubmitted?: boolean;
  depth: number;
  watchedValues: Record<string, unknown>;
  onToggleGroup?: (groupSlug: string, enabled: boolean) => void;
  fieldWarnings?: Record<string, string | null>;
  crossFieldWarnings?: Record<string, string | null>;
  crossFieldWarningStyles?: Record<string, "warning" | "error">;
  crossFieldErrors?: Record<string, string | null>;
  groupValidationErrors?: Record<string, string>;
  fieldValidationErrors?: Record<string, string>;
}

export function FormGroupRenderer({ 
  group, 
  isFieldVisible,
  isGroupVisible,
  renderField, 
  errors, 
  touchedFields = {},
  isSubmitted = false,
  depth,
  watchedValues,
  onToggleGroup,
  fieldWarnings = {},
  crossFieldWarnings = {},
  crossFieldWarningStyles = {},
  crossFieldErrors = {},
  groupValidationErrors = {},
  fieldValidationErrors = {},
}: FormGroupRendererProps) {
  const layoutOptions = group.layout_options || {};
  const layout = layoutOptions.layout || "vertical";
  const cardStyle = layoutOptions.card_style || "default";
  const collapsedByDefault = layoutOptions.collapsed_by_default ?? false;
  const showHeader = layoutOptions.show_header ?? true;
  const childrenPerRow = layoutOptions.children_per_row ?? 1;
  const rawBackgroundColor = layoutOptions.background_color;
  
  // Background colors are only applied in light mode
  const isDarkMode = useIsDarkMode();
  const backgroundColor = isDarkMode ? undefined : rawBackgroundColor;
  
  const [isOpen, setIsOpen] = useState(!collapsedByDefault);
  
  // Get validation error for this group
  // Check if this group has an error (for visual ring) vs should show message
  const groupErrorValue = groupValidationErrors[group.slug];
  const isRingOnly = groupErrorValue === "__RING_ONLY__"; // Children: ring only, no message
  const hasGroupError = isRingOnly; // Ring only for children
  const groupErrorMessage = groupErrorValue && !isRingOnly ? groupErrorValue : ""; // Message only for parent
  
  // Auto-expand group if it contains fields with errors or has a group validation error
  useEffect(() => {
    if (isSubmitted && !isOpen) {
      const allFields = collectAllFieldsFromGroupStatic(group);
      const hasFieldErrors = allFields.some(field => errors[field.slug] || fieldValidationErrors[field.slug] || crossFieldErrors[field.slug]);
      const hasGroupError = group.slug in groupValidationErrors || groupValidationErrors[group.slug] === "__RING_ONLY__";
      if (hasFieldErrors || hasGroupError) {
        setIsOpen(true);
      }
    }
  }, [isSubmitted, errors, group, isOpen, groupValidationErrors, fieldValidationErrors, crossFieldErrors]);
  
  // Check if this group should be visible based on its visibility conditions
  if (!isGroupVisible(group)) {
    return null;
  }
  
  // Check if this group uses manual toggle activation
  const hasManualToggle = group.validation_rules?.activation?.mode === "manual_toggle";
  const toggleLabel = group.validation_rules?.activation?.toggle_label || "Activer ce groupe";
  const toggleSlug = `${group.slug}_enabled`;
  const isToggleEnabled = hasManualToggle ? watchedValues[toggleSlug] === true : true;
  const showFieldsWhenInactive = group.validation_rules?.activation?.show_fields_when_inactive ?? true; // default: show grayed out
  
  // Filter visible fields
  const visibleFields = group.fields.filter(isFieldVisible).sort((a, b) => a.order - b.order);
  
  // Check if this group has any visible content (fields or children with visible fields)
  // Also check that child groups are visible
  const hasVisibleContent = visibleFields.length > 0 || 
    group.children.some(child => 
      isGroupVisible(child) && (child.fields.some(isFieldVisible) || child.children.length > 0)
    );
  
  if (!hasVisibleContent) return null;
  
  const cardStyleClass = getCardStyleClass(cardStyle);
  const layoutClass = getLayoutClass(layout);
  
  const content = (
    <>
      {visibleFields.length > 0 && (
        <div className={`${layoutClass} items-end`}>
          {visibleFields.map((field) => {
            const fieldWidthClass = getFieldWidthClass(field.width, layout);
            return (
            <motion.div
              key={field.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className={`flex flex-col ${fieldWidthClass}`}
            >
              {field.field_type !== "checkbox" && (
                <div className="flex items-start gap-2 mb-2">
                  <Label 
                    htmlFor={field.slug} 
                    className={cn("leading-tight", getLabelClassName(field.label_style))}
                    style={getLabelInlineStyle(field.label_style)}
                  >
                    {parseBBCode(field.label)}
                    {field.required && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  {field.tooltip && (
                    <InfoTooltip content={linkifyText(field.tooltip)} side="top">
                      <button
                        type="button"
                        className="text-muted-foreground cursor-help hover:text-foreground transition-colors flex-shrink-0"
                      >
                        ⓘ
                      </button>
                    </InfoTooltip>
                  )}
                </div>
              )}
              <div className="mt-auto">
                {renderField(field)}
              </div>
              {/* Always reserve space for error/warning messages to maintain alignment */}
              <div className="relative min-h-[1.25rem] mt-1">
                {fieldValidationErrors[field.slug] ? (
                  <p className="text-xs text-destructive absolute top-0 left-0 right-0">
                    {fieldValidationErrors[field.slug]}
                  </p>
                ) : errors[field.slug] && (touchedFields[field.slug] || isSubmitted) ? (() => {
                  const fieldError = errors[field.slug] as { message?: unknown } | undefined;
                  const customMessage =
                    typeof fieldError?.message === "string" && fieldError.message.length > 0
                      ? fieldError.message
                      : null;
                  return (
                    <p className="text-xs text-destructive">
                      {customMessage ??
                        (field.field_type === "number"
                          ? "Veuillez entrer un nombre valide"
                          : field.field_type === "select" || field.field_type === "radio"
                          ? "Veuillez sélectionner une option"
                          : field.field_type === "multiselect"
                          ? "Veuillez sélectionner au moins une option"
                          : field.field_type === "checkbox"
                          ? "Cette case doit être cochée"
                          : field.field_type === "date"
                          ? "Veuillez sélectionner une date"
                          : field.field_type === "textarea"
                          ? "Ce champ ne peut pas être vide"
                          : "Ce champ est requis")}
                    </p>
                  );
                })() : fieldWarnings[field.slug] ? (
                  <p className="text-xs text-destructive">
                    {fieldWarnings[field.slug]}
                  </p>
                ) : crossFieldErrors[field.slug] ? (
                  <p className="text-xs text-destructive font-medium">
                    {crossFieldErrors[field.slug]}
                  </p>
                ) : crossFieldWarnings[field.slug] ? (
                  <p className={`text-xs ${crossFieldWarningStyles[field.slug] === "error" ? "text-destructive font-medium" : "text-yellow-500"}`}>
                    {crossFieldWarnings[field.slug]}
                  </p>
                ) : field.description ? (
                  <p className="text-xs text-muted-foreground">
                    {field.description}
                  </p>
                ) : null}
              </div>
            </motion.div>
            );
          })}
        </div>
      )}
      
      {/* Render child groups */}
      {group.children.length > 0 && (
        <div className={`grid ${getChildrenGridClass(childrenPerRow)} gap-4 ${visibleFields.length > 0 ? "mt-4" : ""}`}>
          {group.children.map((child) => (
            <FormGroupRenderer
              key={child.id}
              group={child}
              isFieldVisible={isFieldVisible}
              isGroupVisible={isGroupVisible}
              renderField={renderField}
              errors={errors}
              touchedFields={touchedFields}
              isSubmitted={isSubmitted}
              depth={depth + 1}
              watchedValues={watchedValues}
              onToggleGroup={onToggleGroup}
              fieldWarnings={fieldWarnings}
              crossFieldWarnings={crossFieldWarnings}
              crossFieldWarningStyles={crossFieldWarningStyles}
              crossFieldErrors={crossFieldErrors}
              groupValidationErrors={groupValidationErrors}
              fieldValidationErrors={fieldValidationErrors}
            />
          ))}
        </div>
      )}
    </>
  );
  
  // Manual toggle header component
  const toggleHeader = hasManualToggle && (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b">
      <Switch
        id={toggleSlug}
        checked={isToggleEnabled}
        onCheckedChange={(checked) => onToggleGroup?.(group.slug, checked)}
      />
      <Label htmlFor={toggleSlug} className="text-sm font-medium cursor-pointer">
        {toggleLabel}
      </Label>
    </div>
  );
  
  // Wrapper for disabled state when manual toggle is off
  const wrapContent = (children: React.ReactNode) => {
    if (hasManualToggle && !isToggleEnabled) {
      // If showFieldsWhenInactive is false, hide content completely
      if (!showFieldsWhenInactive) {
        return null;
      }
      // Otherwise show grayed out
      return (
        <div className="opacity-50 pointer-events-none select-none">
          {children}
        </div>
      );
    }
    return children;
  };
  
  // Visual indicator class for group validation errors
  const groupErrorClass = hasGroupError 
    ? "ring-2 ring-destructive ring-offset-2 ring-offset-background" 
    : "";
  
  // If collapsible feature is enabled and we show header
  if (showHeader && collapsedByDefault) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div 
          id={`group-${group.slug}`}
          className={`${cardStyleClass} ${groupErrorClass}`}
          style={backgroundColor ? { backgroundColor } : undefined}
        >
          {toggleHeader}
          <CollapsibleTrigger asChild>
            <button 
              type="button"
              className="flex items-center gap-2 w-full text-left mb-3 hover:text-primary transition-colors"
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <h4 
                className={cn("text-sm font-medium text-muted-foreground", getLabelClassName(group.label_style))}
                style={getLabelInlineStyle(group.label_style)}
              >
                {parseBBCode(group.name)}
              </h4>
            </button>
          </CollapsibleTrigger>
          {group.description && showHeader && (
            <p className="text-xs text-muted-foreground mb-3 ml-6">
              {group.description}
            </p>
          )}
          {groupErrorMessage && (
            <p className="text-xs text-destructive mb-3 ml-6">
              {groupErrorMessage}
            </p>
          )}
          <CollapsibleContent forceMount className="data-[state=closed]:hidden">
            {wrapContent(content)}
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  }
  
  // Standard render without collapsible
  return (
    <div 
      id={`group-${group.slug}`}
      className={`${cardStyleClass} ${groupErrorClass}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {toggleHeader}
      {showHeader && (
        <>
          <h4 
            className={cn("text-sm font-medium text-muted-foreground mb-3", getLabelClassName(group.label_style))}
            style={getLabelInlineStyle(group.label_style)}
          >
            {parseBBCode(group.name)}
          </h4>
          {group.description && (
            <p className="text-xs text-muted-foreground mb-3">
              {group.description}
            </p>
          )}
        </>
      )}
      {groupErrorMessage && (
        <p className="text-xs text-destructive mb-3">
          {groupErrorMessage}
        </p>
      )}
      {wrapContent(content)}
    </div>
  );
}
